import { useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ACCESS_REQUEST_STATUSES } from '@infras/shared-types';
import { ACCESS_REQUEST_FIELDS, statusBadgeClass } from '../lib/access';
import type { AccessRequest } from '../lib/access';
import { Pager, usePaged } from '../components/Pager';
import { useMe } from '../lib/useMe';
import { formatDate, formatDateTime, formatDuration } from '../lib/format';

// Visibility and review permissions remain enforced by the API and detail page.
const QUEUE = gql`
  ${ACCESS_REQUEST_FIELDS}
  query AccessRequests($status: String) {
    accessRequests(status: $status) {
      ...AccessRequestFields
      assignees {
        reviewerEmail
        layer
      }
    }
  }
`;

export function AccessRequestsPage() {
  return (
    <div className="page access-requests-page">
      <div className="detail-head access-requests-head">
        <div>
          <h1>Access requests</h1>
          <p className="detail-desc">
            Request company resources and follow each review in one place.
          </p>
        </div>
        <div className="btn-row">
          <Link to="/docs/request-access" className="btn btn-ghost">
            Request guide
          </Link>
          <Link to="/access/new" className="btn btn-primary">
            + New request
          </Link>
        </div>
      </div>
      <AllRequests />
    </div>
  );
}

function AllRequests() {
  const { me, loading: meLoading } = useMe();
  const myEmail = (me?.email ?? '').toLowerCase();
  const [status, setStatus] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(20);
  const { data, loading, error, refetch } = useQuery<{
    accessRequests: AccessRequest[];
  }>(QUEUE, {
    variables: { status: status || null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const all = data?.accessRequests ?? [];
  const query = search.trim().toLowerCase();
  const rows = all.filter(
    (r) =>
      (!mineOnly || r.requesterEmail.toLowerCase() === myEmail) &&
      (!query ||
        [
          r.resourceName,
          r.resourceType,
          `#${r.id}`,
          r.requesterEmail,
          r.maintainerEmail,
        ].some((value) => value.toLowerCase().includes(query))),
  );
  const { pageItems, page, pageCount, setPage, total, start, pageSize } =
    usePaged(rows, perPage);
  const filtered = Boolean(status || mineOnly || search);
  const initialLoading = (loading && !data) || (mineOnly && meLoading);
  const clearFilters = () => {
    setStatus('');
    setMineOnly(false);
    setSearch('');
    setPage(1);
  };

  return (
    <section
      className="list-panel access-requests-panel"
      aria-label="Access requests"
    >
      <div className="filter-row access-requests-filters">
        <input
          type="search"
          aria-label="Search requests"
          placeholder="Search resource, person, or #ID"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="class-select"
          aria-label="Filter by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {ACCESS_REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <label className="access-requests-mine">
          <input
            type="checkbox"
            checked={mineOnly}
            disabled={!me}
            onChange={(e) => {
              setMineOnly(e.target.checked);
              setPage(1);
            }}
          />
          Only my requests
        </label>
        {filtered && (
          <button className="btn btn-ghost" onClick={clearFilters}>
            Clear filters
          </button>
        )}
        <div className="access-requests-tools">
          <div className="access-requests-summary" role="status">
            {initialLoading
              ? 'Loading requests…'
              : error
                ? 'Requests could not be refreshed'
                : `${total} ${total === 1 ? 'request' : 'requests'}${mineOnly ? ' from you' : ''}${loading ? ' · Updating…' : ''}`}
          </div>

          <button
            className="btn"
            type="button"
            disabled={loading}
            onClick={() => refetch().catch(() => undefined)}
          >
            Refresh
          </button>
        </div>
      </div>
      {error && (
        <div className="access-requests-error" role="alert">
          <div>
            <strong>Unable to load requests</strong>
            <p>
              {data
                ? 'Showing the last loaded results. Try refreshing again.'
                : 'Try again to load the request list.'}
            </p>
          </div>
          <button
            className="btn"
            disabled={loading}
            onClick={() => {
              void refetch().catch(() => undefined);
            }}
          >
            {loading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}
      {initialLoading ? (
        <div
          className="skeleton-row-group access-requests-loading"
          aria-hidden="true"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        !error && (
          <div className="empty-state access-requests-empty">
            <div className="empty-title">
              {filtered ? 'No matching requests' : 'No access requests yet'}
            </div>
            <p className="empty-body">
              {filtered
                ? 'Try a different search or clear your filters to see more requests.'
                : 'Create a request to get access to a company resource. You can track its review here.'}
            </p>
            <div className="empty-actions">
              {filtered ? (
                <button className="btn" onClick={clearFilters}>
                  Clear filters
                </button>
              ) : (
                <Link to="/access/new" className="btn">
                  Create a request
                </Link>
              )}
            </div>
          </div>
        )
      ) : (
        <>
          <RequestTable rows={pageItems} showRequester={!mineOnly} />
          <div className="access-requests-footer">
            <select
              className="class-select"
              aria-label="Rows per page"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <Pager
              page={page}
              pageCount={pageCount}
              total={total}
              start={start}
              pageSize={pageSize}
              onPage={setPage}
            />
          </div>
        </>
      )}
    </section>
  );
}

function RequestTable({
  rows,
  showRequester,
}: {
  rows: AccessRequest[];
  showRequester: boolean;
}) {
  return (
    <div
      className="data-list-wrap"
      role="region"
      aria-label="Request results"
      tabIndex={0}
    >
      <table
        className={`data-list--primary access-req-table${showRequester ? '' : ' access-req-table--mine'}`}
        aria-label="Access request results"
      >
        <colgroup>
          <col className="access-col-resource" />
          {showRequester && <col className="access-col-person" />}
          <col className="access-col-status" />
          <col className="access-col-duration" />
          <col className="access-col-person" />
          <col className="access-col-created" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Resource</th>
            {showRequester && <th scope="col">Requester</th>}
            <th scope="col">Status</th>
            <th scope="col">Duration</th>
            <th scope="col">Maintainer</th>
            <th scope="col">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="access-req-resource">
                <Link
                  to={`/access/${r.id}`}
                  className="access-req-resource-link table-title"
                  title={r.resourceName}
                >
                  <span className="access-req-resource-name">
                    {r.resourceName}
                  </span>
                </Link>
                <div className="access-req-meta">
                  <Link to={`/access/${r.id}`} aria-label={`Request ${r.id}`}>
                    #{r.id}
                  </Link>
                  <span>· {r.resourceType}</span>
                  {r.riskTier && r.riskTier !== 'standard' && (
                    <span className={`risk-badge risk-badge--${r.riskTier}`}>
                      {r.riskTier}
                    </span>
                  )}
                </div>
              </td>
              {showRequester && (
                <td>
                  <span className="access-req-person" title={r.requesterEmail}>
                    {r.requesterEmail}
                  </span>
                </td>
              )}
              <td>
                <span
                  title={`Review layer ${r.currentLayer}`}
                  className={statusBadgeClass(r.status)}
                >
                  {r.status}
                </span>
                {r.status === 'pending' && (
                  <div className="access-req-meta">Layer {r.currentLayer}</div>
                )}
              </td>
              <td>
                <div>
                  {r.durationKind === 'temporary' ? 'Temporary' : 'Permanent'}
                </div>
                {r.durationKind === 'temporary' && r.expiresAt && (
                  <div className="access-req-meta">
                    Until {formatDate(r.expiresAt)}
                  </div>
                )}
              </td>
              <td>
                <span className="access-req-person" title={r.maintainerEmail}>
                  {r.maintainerEmail || '—'}
                </span>
              </td>
              <td>
                <time
                  dateTime={r.createdAt}
                  title={formatDateTime(r.createdAt)}
                >
                  {formatDate(r.createdAt)}
                </time>
                {r.decidedAt && (
                  <div className="access-req-meta">
                    Took {formatDuration(r.createdAt, r.decidedAt)}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
